import { useCallback, useEffect, useRef, useState } from "react";
import type { JoinRoomResponse, StreamItem } from "../../types/meeting.types";
import { loadDevice } from "./device";
import { createSendTransport } from "./sendTransport";
import { createRecvTransport } from "./recvTransport";
import {
  getAllHostMediaStreams,
  getRnnoiseAudioStream,
  getUserMedia,
} from "./media";
import { consumeProducer } from "./consumerProducer";
import { socket } from "./socket";
import { VideoGrid } from "../mediasoup-ui/VideoGrid";
import { useMeetingStore } from "../../store/meeting.store";
import { useWebexMeetingStore } from "../../store/webex.store";
import { subscribeToMediaDeviceChange } from "../../utils/mediaDeviceChange";
import logger from "../../utils/logger";
import type { RaisedHand } from "../../hooks/useRaisedHands";



const updateMediaState = (
  s: StreamItem,
  producerId: string,
  state: Partial<StreamItem>,
  userId?: string,
): StreamItem => {
  const isAudioProducer = s.audioProducerId === producerId;
  const isVideoProducer = s.videoProducerId === producerId;
  const isSameUser = !!userId && String(s.userId) === String(userId);
  const shouldApplyAudioState = isAudioProducer || isSameUser;

  if (!shouldApplyAudioState && !isVideoProducer) {
    return s;
  }
  const patch: Partial<StreamItem> = {};

  if (shouldApplyAudioState && state.audioPaused !== undefined) {
    patch.audioPaused = state.audioPaused;
  }

  if (isVideoProducer && state.videoPaused !== undefined) {
    patch.videoPaused = state.videoPaused;
  }
  if (state.frozen !== undefined) {
    patch.frozen = state.frozen;
  }

  if (state.blurred !== undefined) {
    patch.blurred = state.blurred;
  }

  return {
    ...s,
    ...patch,
  };
};

const updateMicLockState = (
  s: StreamItem,
  producerId: string,
  locked: boolean,
): StreamItem => {
  return s.audioProducerId === producerId ? { ...s, micLocked: locked } : s;
};

const updateSpotlightState = (
  s: StreamItem,
  producerId: string,
): StreamItem => {
  const match =
    s.videoProducerId === producerId || s.audioProducerId === producerId;

  return {
    ...s,
    spotlight: !!producerId && match,
  };
};

const handleMediaStateUpdated = (
  producerId: string,
  state: Partial<StreamItem>,
  setStreams: React.Dispatch<React.SetStateAction<StreamItem[]>>,
  userId?: string,
) => {
  setStreams((prev) =>
    prev.map((s) => updateMediaState(s, producerId, state, userId)),
  );
};

const handleMicLockUpdated = (
  producerId: string,
  locked: boolean,
  setStreams: React.Dispatch<React.SetStateAction<StreamItem[]>>,
) => {
  setStreams((prev) =>
    prev.map((s) => updateMicLockState(s, producerId, locked)),
  );
};

const handleSpotlightUpdated = (
  producerId: string,
  setStreams: React.Dispatch<React.SetStateAction<StreamItem[]>>,
) => {
  setStreams((prev) => prev.map((s) => updateSpotlightState(s, producerId)));
};

const handleBulkMediaStateUpdate = (
  type: string,
  setStreams: React.Dispatch<React.SetStateAction<StreamItem[]>>,
) => {
  if (type === "mute-all") {
    setStreams((prev) =>
      prev.map((s) => ({
        ...s,
        audioPaused: true,
      })),
    );
  }

  if (type === "unmute-all") {
    setStreams((prev) =>
      prev.map((s) => ({
        ...s,
        audioPaused: false,
      })),
    );
  }
};

const removeClosedProducerStreams = (s: StreamItem, producerId: string) => {
  return s.videoProducerId !== producerId && s.audioProducerId !== producerId;
};

const onProducerClosedHandler = (
  producerId: string,
  consumersRef: any,
  consumedProducerIdsRef: any,
  setStreams: React.Dispatch<React.SetStateAction<StreamItem[]>>,
) => {
  const consumer = consumersRef.current.get(producerId);

  if (consumer) {
    consumer.close();
    consumersRef.current.delete(producerId);
  }

  consumedProducerIdsRef.current.delete(producerId);

  setStreams((prev) =>
    prev.filter((s) => removeClosedProducerStreams(s, producerId)),
  );
};

const getVideoDeviceId = (track: MediaStreamTrack) =>
  track.getSettings().deviceId || "";

const getAudioDeviceId = (track: MediaStreamTrack) =>
  track.getSettings().deviceId || "";

const getStreamVideoDeviceId = (stream: MediaStream) =>
  (stream as any).__videoDeviceId ||
  getVideoDeviceId(stream.getVideoTracks()[0]) ||
  "";

const getStreamAudioDeviceId = (stream: MediaStream) =>
  (stream as any).__audioDeviceId ||
  getAudioDeviceId(stream.getAudioTracks()[0]) ||
  "";

const MeetingMediaSoup = ({ raisedHands = [] }: { raisedHands?: RaisedHand[] }) => {
  const userData = useMeetingStore((s) => s.userData);
  const cameraAssignments = useMeetingStore((s) => s.cameraAssignments);
  const micAssignments = useMeetingStore((s) => s.micAssignments);
  const joining = useMeetingStore((s) => s.mediaJoining);
  const setJoining = useMeetingStore((s) => s.setMediaJoining);
  const localMicOn = useMeetingStore((s) => s.localMicOn);
  const assignCamera = useMeetingStore((s) => s.assignCamera);
  const assignMic = useMeetingStore((s) => s.assignMic);
  const [streams, setStreams] = useState<StreamItem[]>([]);
  const advocteActiveCase = useMeetingStore((s) => s.advocteActiveCase);
  const remoteStreams = useWebexMeetingStore((s) => s.remoteStreams);
  const setLocalStreams = useMeetingStore((s) => s.setLocalStreams);

  const audioProducerRef = useRef<any>(null);
  const videoProducerRef = useRef<any>(null);
  const producersRef = useRef<Map<string, any>>(new Map());
  const videoProducerDeviceIdsRef = useRef<Map<string, string>>(new Map());
  const hiddenLocalVideoStreamsRef = useRef<Map<string, StreamItem>>(new Map());
  const replacingVideoProducerIdsRef = useRef<Set<string>>(new Set());
  const replacingAssignedCameraUsersRef = useRef<Set<string>>(new Set());
  const replacingAssignedMicUsersRef = useRef<Set<string>>(new Set());

  const sendTransportRef = useRef<any>(null);
  const recvTransportRef = useRef<any>(null);

  // 🔥 SOURCE OF TRUTH
  const consumedProducerIdsRef = useRef<Set<string>>(new Set());
  const streamsRef = useRef<StreamItem[]>([]);

  const consumersRef = useRef<Map<string, any>>(new Map());
  const audioElementsRef = useRef<Map<string, HTMLAudioElement>>(new Map());

  const mediaStatesRef = useRef<any>({});

  const applyLocalMicState = useCallback(
    async (nextMicOn: boolean) => {
      if (userData.role === "Judge") return;

      const producer = audioProducerRef.current;
      const producerId = producer?.id;

      streamsRef.current.forEach((stream) => {
        if (!stream.isLocal) return;
        stream.stream
          .getAudioTracks()
          .forEach((track) => (track.enabled = nextMicOn));
      });

      if (producer) {
        if (nextMicOn && producer.paused) {
          await producer.resume();
        }
        if (!nextMicOn && !producer.paused) {
          await producer.pause();
        }
      }

      setStreams((prev) =>
        prev.map((stream) =>
          stream.isLocal &&
          (!producerId || stream.audioProducerId === producerId)
            ? { ...stream, audioPaused: !nextMicOn }
            : stream,
        ),
      );

      if (producerId) {
        socket.emit("toggle-media", {
          roomId: userData?.roomId,
          producerId,
          kind: "audio",
          action: nextMicOn ? "resume" : "pause",
          // message: nextMicOn ? "Microphone unmuted." : "Microphone muted.",
        });
      }
    },
    [userData?.role, userData?.roomId],
  );

  useEffect(() => {
    streamsRef.current = streams;
  }, [streams]);

  useEffect(() => {
    void applyLocalMicState(localMicOn);
  }, [applyLocalMicState, localMicOn]);

  const setLocalVideoPaused = useCallback(
    (producerId: string, paused: boolean) => {
      setStreams((prev) =>
        prev.map((stream) =>
          stream.videoProducerId === producerId
            ? { ...stream, videoPaused: paused }
            : stream,
        ),
      );
    },
    [],
  );

  const removeLocalVideoFrame = useCallback((producerId: string) => {
    setStreams((prev) =>
      prev.filter((stream) => {
        if (stream.videoProducerId !== producerId) return true;

        hiddenLocalVideoStreamsRef.current.set(producerId, {
          ...stream,
          videoPaused: true,
        });

        stream.stream.getVideoTracks().forEach((track) => {
          if (track.readyState !== "ended") {
            track.stop();
          }
        });

        return false;
      }),
    );
  }, []);

  const getReplacementVideoTrack = useCallback(
    async (producerId: string) => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(
        (device) => device.kind === "videoinput",
      );
      const currentDeviceId = videoProducerDeviceIdsRef.current.get(producerId);
      const assignedDeviceIds = new Set(
        Array.from(videoProducerDeviceIdsRef.current.entries())
          .filter(([id]) => id !== producerId)
          .map(([, deviceId]) => deviceId)
          .filter(Boolean),
      );
      const camera =
        userData.role === "Judge"
          ? videoInputs.find(
              (device) =>
                currentDeviceId &&
                device.deviceId === currentDeviceId &&
                !assignedDeviceIds.has(device.deviceId),
            ) ||
            videoInputs.find(
              (device) =>
                device.deviceId && !assignedDeviceIds.has(device.deviceId),
            )
          : videoInputs.find((device) => device.deviceId === currentDeviceId) ||
            videoInputs[0];

      if (!camera) return null;

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: camera.deviceId ? { exact: camera.deviceId } : undefined,
        },
        audio: false,
      });

      const track = stream.getVideoTracks()[0] || null;

      if (track) {
        videoProducerDeviceIdsRef.current.set(
          producerId,
          getVideoDeviceId(track) || camera.deviceId,
        );
      }

      return track;
    },
    [userData.role],
  );

  const emitLocalVideoState = useCallback(
    (producerId: string, paused: boolean) => {
      const stream =
        streamsRef.current.find(
          (item) => item.videoProducerId === producerId,
        ) || hiddenLocalVideoStreamsRef.current.get(producerId);

      if (!stream) return;

      socket.emit("toggle-media", {
        userId: stream.userId,
        producerId,
        kind: "video",
        action: paused ? "pause" : "resume",
        message: paused ? "Camera disconnected." : "Camera reconnected.",
      });
    },
    [],
  );

  const handleRemovedVideoTrack = useCallback(
    (producerId: string) => {
      setLocalVideoPaused(producerId, true);
      emitLocalVideoState(producerId, true);
      removeLocalVideoFrame(producerId);
    },
    [emitLocalVideoState, removeLocalVideoFrame, setLocalVideoPaused],
  );

  const replaceEndedVideoTrack = useCallback(
    async (producerId: string) => {
      if (replacingVideoProducerIdsRef.current.has(producerId)) return;

      replacingVideoProducerIdsRef.current.add(producerId);
      setLocalVideoPaused(producerId, true);

      try {
        await new Promise((resolve) => setTimeout(resolve, 300));

        const replacementTrack = await getReplacementVideoTrack(producerId);
        const producer = producersRef.current.get(producerId);

        if (!replacementTrack || !producer) {
          emitLocalVideoState(producerId, true);
          removeLocalVideoFrame(producerId);
          return;
        }

        await producer.replaceTrack({ track: replacementTrack });
        if (producer.paused) {
          await producer.resume();
        }

        replacementTrack.onended = () => handleRemovedVideoTrack(producerId);
        replacementTrack.onmute = () => {
          window.setTimeout(() => {
            if (replacementTrack.muted) {
              handleRemovedVideoTrack(producerId);
            }
          }, 500);
        };

        setStreams((prev) => {
          let restoredHiddenStream = false;
          const nextStreams = prev.map((stream) => {
            if (stream.videoProducerId !== producerId) return stream;
            restoredHiddenStream = true;

            stream.stream.getVideoTracks().forEach((existingTrack) => {
              if (existingTrack.id !== replacementTrack.id) {
                existingTrack.stop();
              }
            });

            const nextStream = new MediaStream([
              ...stream.stream.getAudioTracks(),
              replacementTrack,
            ]);

            return {
              ...stream,
              stream: nextStream,
              videoPaused: false,
            };
          });

          if (!restoredHiddenStream) {
            const hiddenStream =
              hiddenLocalVideoStreamsRef.current.get(producerId);

            if (hiddenStream) {
              nextStreams.push({
                ...hiddenStream,
                stream: new MediaStream([
                  ...hiddenStream.stream.getAudioTracks(),
                  replacementTrack,
                ]),
                videoPaused: false,
              });
            }
          }

          hiddenLocalVideoStreamsRef.current.delete(producerId);

          return nextStreams;
        });
        emitLocalVideoState(producerId, false);
      } catch (error) {
        logger.error("Failed to replace removed camera:", error);
        emitLocalVideoState(producerId, true);
        removeLocalVideoFrame(producerId);
      } finally {
        replacingVideoProducerIdsRef.current.delete(producerId);
      }
    },
    [
      emitLocalVideoState,
      getReplacementVideoTrack,
      handleRemovedVideoTrack,
      removeLocalVideoFrame,
      setLocalVideoPaused,
    ],
  );

  const attachVideoTrackEndedHandler = useCallback(
    (producerId: string, track: MediaStreamTrack) => {
      track.onended = () => handleRemovedVideoTrack(producerId);
      track.onmute = () => {
        window.setTimeout(() => {
          if (track.muted) {
            handleRemovedVideoTrack(producerId);
          }
        }, 500);
      };
    },
    [handleRemovedVideoTrack],
  );

  const replaceAssignedCamera = useCallback(
    async (userId: string, deviceId: string) => {
      if (!deviceId || replacingAssignedCameraUsersRef.current.has(userId)) {
        return;
      }

      const streamItem =
        streamsRef.current.find(
          (stream) => stream.isLocal && stream.userId === userId,
        ) ||
        Array.from(hiddenLocalVideoStreamsRef.current.values()).find(
          (stream) => stream.isLocal && stream.userId === userId,
        );

      const producerId = streamItem?.videoProducerId;
      const producer = producerId ? producersRef.current.get(producerId) : null;

      if (!streamItem || !producerId || !producer) return;

      const currentTrack = streamItem.stream.getVideoTracks()[0];
      const currentDeviceId = getStreamVideoDeviceId(streamItem.stream);
      if (
        currentDeviceId === deviceId &&
        currentTrack &&
        currentTrack.readyState !== "ended" &&
        !streamItem.videoPaused
      ) {
        return;
      }

      replacingAssignedCameraUsersRef.current.add(userId);

      try {
        const nextStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId } },
          audio: false,
        });
        const nextTrack = nextStream.getVideoTracks()[0];

        if (!nextTrack) return;

        await producer.replaceTrack({ track: nextTrack });
        if (producer.paused) {
          await producer.resume();
        }

        videoProducerDeviceIdsRef.current.set(
          producerId,
          getVideoDeviceId(nextTrack) || deviceId,
        );
        attachVideoTrackEndedHandler(producerId, nextTrack);

        setStreams((prev) => {
          let updatedExistingStream = false;
          const updatedStreams = prev.map((stream) => {
            if (stream.videoProducerId !== producerId) return stream;

            updatedExistingStream = true;
            stream.stream.getVideoTracks().forEach((track) => {
              if (track.id !== nextTrack.id) {
                track.stop();
              }
            });

            return {
              ...stream,
              stream: new MediaStream([
                ...stream.stream.getAudioTracks(),
                nextTrack,
              ]),
              videoPaused: false,
            };
          });

          if (!updatedExistingStream) {
            const hiddenStream =
              hiddenLocalVideoStreamsRef.current.get(producerId);

            if (hiddenStream) {
              updatedStreams.push({
                ...hiddenStream,
                stream: new MediaStream([
                  ...hiddenStream.stream.getAudioTracks(),
                  nextTrack,
                ]),
                videoPaused: false,
              });
            }
          }

          hiddenLocalVideoStreamsRef.current.delete(producerId);

          return updatedStreams;
        });
        emitLocalVideoState(producerId, false);
      } catch (error) {
        logger.error("Failed to replace selected camera:", error);
      } finally {
        replacingAssignedCameraUsersRef.current.delete(userId);
      }
    },
    [attachVideoTrackEndedHandler, emitLocalVideoState],
  );

  const replaceAssignedMic = useCallback(
    async (userId: string, deviceId: string) => {
      if (!deviceId || replacingAssignedMicUsersRef.current.has(userId)) {
        return;
      }

      const streamItem = streamsRef.current.find(
        (stream) => stream.isLocal && stream.userId === userId,
      );
      const producerId = streamItem?.audioProducerId;
      const producer = producerId ? producersRef.current.get(producerId) : null;

      if (!streamItem || !producerId || !producer) return;

      const currentTrack = streamItem.stream.getAudioTracks()[0];
      const currentDeviceId = getStreamAudioDeviceId(streamItem.stream);
      if (
        currentDeviceId === deviceId &&
        currentTrack &&
        currentTrack.readyState !== "ended" &&
        !streamItem.audioPaused
      ) {
        return;
      }

      replacingAssignedMicUsersRef.current.add(userId);

      try {
        const nextStream = await getRnnoiseAudioStream(deviceId);
        const nextTrack = nextStream.getAudioTracks()[0];

        if (!nextTrack) return;

        await producer.replaceTrack({ track: nextTrack });

        setStreams((prev) =>
          prev.map((stream) => {
            if (stream.audioProducerId !== producerId) return stream;

            stream.stream.getAudioTracks().forEach((track) => {
              if (track.id !== nextTrack.id) {
                track.stop();
              }
            });

            const updatedStream = new MediaStream([
              nextTrack,
              ...stream.stream.getVideoTracks(),
            ]);
            (updatedStream as any).__audioDeviceId =
              getAudioDeviceId(nextTrack) || deviceId;
            (updatedStream as any).__videoDeviceId = getStreamVideoDeviceId(
              stream.stream,
            );

            return {
              ...stream,
              stream: updatedStream,
            };
          }),
        );
      } catch (error) {
        logger.error("Failed to replace selected microphone:", error);
      } finally {
        replacingAssignedMicUsersRef.current.delete(userId);
      }
    },
    [],
  );

  useEffect(() => {
    Object.entries(cameraAssignments).forEach(([userId, deviceId]) => {
      replaceAssignedCamera(userId, deviceId);
    });
  }, [cameraAssignments, replaceAssignedCamera]);

  useEffect(() => {
    Object.entries(micAssignments).forEach(([userId, deviceId]) => {
      replaceAssignedMic(userId, deviceId);
    });
  }, [micAssignments, replaceAssignedMic]);

  async function produceStream(
    stream: MediaStream,
    roleInfo: {
      userId: string;
      name: string;
      role: string;
    },
  ) {
    const producers: any = {
      audio: null,
      video: null,
    };

    for (const track of stream.getTracks()) {
      const producer = await sendTransportRef.current.produce({
        track,
        codecOptions: {
          opusStereo: 1,
          opusDtx: 1,
          opusFec: 1,
        },
        appData: {
          userId: roleInfo.userId,
          name: roleInfo.name,
          role: roleInfo.role,
          audioPaused:
            track.kind === "audio"
              ? roleInfo.role === "Judge" ||
                roleInfo.role === "Prosecutor" ||
                roleInfo.role === "Defendant" ||
                roleInfo.role === "Teams"
                ? false
                : !localMicOn
              : undefined,
          videoPaused: track.kind === "video" ? false : undefined,
        },
      });
      producersRef.current.set(producer.id, producer);

      if (track.kind === "audio") {
        producers.audio = producer;
        audioProducerRef.current = producer;
      }

      if (track.kind === "video") {
        producers.video = producer;
        videoProducerRef.current = producer;
        videoProducerDeviceIdsRef.current.set(
          producer.id,
          getVideoDeviceId(track),
        );
        attachVideoTrackEndedHandler(producer.id, track);
      }
    }

    return producers;
  }

  const roleMap = [
    {
      name: `Hon'ble Judge ${userData.name}`,
      userId: userData._id,
      role: "Judge",
    },
    {
      name: "Counsel for Petitioner",
      userId: "prosecutor",
      role: "Prosecutor",
    },
    {
      name: "Counsel for Respondent",
      userId: "defendant",
      role: "Defendant",
    },
    {
      name: "Teams",
      userId: "teams",
      role: "Teams",
    },
  ];
  /* ================================
     JOIN ROOM
  ================================= */
  const onJoined = async ({
    rtpCapabilities,
    existingProducers,
    mediaStates,
  }: JoinRoomResponse) => {
    try {
      setJoining(true); // start loading

      await loadDevice(rtpCapabilities as any);

      mediaStatesRef.current = mediaStates;

      sendTransportRef.current = await createSendTransport();
      recvTransportRef.current = await createRecvTransport();

      /* ---------------- HOST ---------------- */
      if (userData.role === "Judge") {
        const hostStreams = await getAllHostMediaStreams();
        // console.log("hostStreams", hostStreams);
        // Register all host streams so ControlBar can stop all tracks on end
        setLocalStreams(hostStreams);

        for (let i = 0; i < hostStreams.length; i++) {
          const stream = hostStreams[i];
          const roleInfo = roleMap[i];

          const producers = await produceStream(stream, roleInfo);
          const audioDeviceId = getStreamAudioDeviceId(stream);
          const videoDeviceId = getStreamVideoDeviceId(stream);

          if (audioDeviceId) {
            assignMic(roleInfo.userId, audioDeviceId);
          }

          if (videoDeviceId) {
            assignCamera(roleInfo.userId, videoDeviceId);
          }

          setStreams((prev) => [
            ...prev,
            {
              userId: roleInfo.userId,
              name: roleInfo.name,
              stream,
              isLocal: true,
              localMute: false,
              audioPaused: false,
              producerId: producers.video?.id,
              videoProducerId: producers.video?.id,
              audioProducerId: producers.audio?.id,
              role: roleInfo.role,
            },
          ]);
        }
      } else if (userData.role !== "Reader") {
        /* ---------------- PARTICIPANT ---------------- */
        const localStream = await getUserMedia();
        localStream
          .getAudioTracks()
          .forEach((track) => (track.enabled = localMicOn));
        // Register in store so ControlBar.onLeaveCall can stop tracks
        setLocalStreams([localStream]);
        const audioDeviceId = getStreamAudioDeviceId(localStream);
        const videoDeviceId = getStreamVideoDeviceId(localStream);

        let audioProducer: any;
        let videoProducer: any;

        for (const track of localStream.getTracks()) {
          const producer = await sendTransportRef.current.produce({
            track,
            appData: {
              userId: userData._id,
              name: userData.name,
              role: userData.role,
              audioPaused: track.kind === "audio" ? !localMicOn : undefined,
              videoPaused: track.kind === "video" ? false : undefined,
            },
          });
          producersRef.current.set(producer.id, producer);

          if (track.kind === "audio") {
            audioProducer = producer;
            audioProducerRef.current = producer;
          }
          if (track.kind === "video") {
            videoProducer = producer;
            videoProducerRef.current = producer;
            videoProducerDeviceIdsRef.current.set(
              producer.id,
              getVideoDeviceId(track),
            );
            attachVideoTrackEndedHandler(producer.id, track);
          }
        }

        if (audioProducer && !localMicOn) {
          await audioProducer.pause();
          socket.emit("toggle-media", {
            roomId: userData?.roomId,
            producerId: audioProducer.id,
            kind: "audio",
            action: "pause",
            message: "Microphone muted.",
          });
        }

        setStreams([
          {
            userId: userData._id || "",
            producerId: videoProducer?.id,
            stream: localStream,
            isLocal: true,
            localMute: true,
            name: userData.name,
            videoProducerId: videoProducer?.id,
            audioProducerId: audioProducer?.id,
            audioPaused: !localMicOn,
            role: userData.role,
          },
        ]);

        if (audioDeviceId) {
          assignMic(userData._id, audioDeviceId);
        }

        if (videoDeviceId) {
          assignCamera(userData._id, videoDeviceId);
        }
      }

      /* ---------------- CONSUME EXISTING ---------------- */
      for (const producerId of existingProducers) {
        await consumeProducer({
          producerId,
          recvTransport: recvTransportRef.current,
          consumedProducerIdsRef,
          socket,
          consumersRef,
          setStreams,
          audioElementsRef,
          mediaStatesRef,
        });
      }
    } finally {
      setJoining(false); // ✅ stop loading
    }
  };

  /* ================================
     NEW PRODUCER JOINED
  ================================= */
  useEffect(() => {
    const onNewProducer = async ({ producerId, state }: any) => {
      if (!recvTransportRef.current) return;
      if (state) {
        mediaStatesRef.current = {
          ...mediaStatesRef.current,
          [producerId]: {
            ...(mediaStatesRef.current?.[producerId] || {}),
            ...state,
          },
        };
      }

      await consumeProducer({
        producerId,
        recvTransport: recvTransportRef.current,
        consumedProducerIdsRef,
        socket,
        consumersRef,
        setStreams,
        audioElementsRef,
        mediaStatesRef,
      });
    };

    socket.on("new-producer", onNewProducer);

    return () => {
      socket.off("new-producer", onNewProducer);
    };
  }, []);

  useEffect(() => {
    const onMediaStateUpdated = ({ producerId, state, userId }: any) => {
      mediaStatesRef.current = {
        ...mediaStatesRef.current,
        [producerId]: {
          ...(mediaStatesRef.current?.[producerId] || {}),
          ...state,
        },
      };

      const meetingState = useMeetingStore.getState();
      const isCurrentUser =
        !!userId && String(userId) === String(meetingState.userData?._id || "");

      if (isCurrentUser && state?.audioPaused !== undefined) {
        meetingState.setLocalMicOn(!state.audioPaused);
      }

      handleMediaStateUpdated(producerId, state, setStreams, userId);
    };

    const onMicLockUpdated = ({ producerId, locked }: any) => {
      const meetingState = useMeetingStore.getState();
      const currentUserId = String(meetingState.userData?._id || "");
      const isLocalMicProducer = streamsRef.current.some(
        (stream) =>
          stream.isLocal &&
          stream.audioProducerId === producerId &&
          String(stream.userId) === currentUserId,
      );

      if (isLocalMicProducer) {
        meetingState.setLocalMicLocked(locked);
        if (locked) {
          meetingState.setLocalMicOn(false);
        }
      }

      handleMicLockUpdated(producerId, locked, setStreams);
    };

    const onSpotlightUpdated = ({ producerId }: any) => {
      handleSpotlightUpdated(producerId, setStreams);
    };

    const onBulkMediaStateUpdated = ({ type }: any) => {
      handleBulkMediaStateUpdate(type, setStreams);
    };

    socket.on("media-state-updated", onMediaStateUpdated);
    socket.on("mic-lock-updated", onMicLockUpdated);
    socket.on("spotlight-updated", onSpotlightUpdated);
    socket.on("media-state-bulk-update", onBulkMediaStateUpdated);

    return () => {
      socket.off("media-state-updated", onMediaStateUpdated);
      socket.off("mic-lock-updated", onMicLockUpdated);
      socket.off("spotlight-updated", onSpotlightUpdated);
      socket.off("media-state-bulk-update", onBulkMediaStateUpdated);
    };
  }, []);

  /* ================================
     PRODUCER CLOSED (USER LEFT)
  ================================= */
  useEffect(() => {
    const onProducerClosed = ({ producerId }: any) => {
      onProducerClosedHandler(
        producerId,
        consumersRef,
        consumedProducerIdsRef,
        setStreams,
      );
    };

    socket.on("producer-closed", onProducerClosed);

    return () => {
      socket.off("producer-closed", onProducerClosed);
    };
  }, []);

  useEffect(() => {
    return subscribeToMediaDeviceChange(() => {
      let removedActiveVideo = false;

      streamsRef.current.forEach((stream) => {
        if (!stream.isLocal || !stream.videoProducerId) return;

        const videoTrack = stream.stream.getVideoTracks()[0];
        if (
          !videoTrack ||
          videoTrack.readyState === "ended" ||
          videoTrack.muted
        ) {
          removedActiveVideo = true;
          handleRemovedVideoTrack(stream.videoProducerId);
        }
      });

      if (removedActiveVideo) return;

      hiddenLocalVideoStreamsRef.current.forEach((stream) => {
        if (stream.videoProducerId) {
          replaceEndedVideoTrack(stream.videoProducerId);
        }
      });
    });
  }, [handleRemovedVideoTrack, replaceEndedVideoTrack]);

  useEffect(() => {
    socket?.emit(
      "join-room",
      {
        roomId: userData?.roomId,
        role: userData?.role,
        name: userData?.name,
        cnr: advocteActiveCase?.cnr,
        userId: userData?._id,
      },
      onJoined,
    );
    return () => {
      socket.off("join-room");
    };
  }, [userData, socket]);

  if (joining) {
    return null;
  }

  const mergedStreams: any[] = [...streams, ...remoteStreams];

  return (
    <div className="h-full min-h-0 overflow-hidden">
      {/* <WebexSessionManager streams={streams} /> */}
      <VideoGrid streams={mergedStreams} raisedHands={raisedHands} />
    </div>
  );
};

export default MeetingMediaSoup;
